import { AxiosResponse } from 'axios';

import { HttpClient } from 'api/HttpClient';
import {
  AccountDto,
  InvitedUser,
  SendUpdateEmailVerificationCodeRequestDto,
  SessionInfoDto,
  SignInDto,
  SignUpDto,
  SuccessResponse,
  UpdatePasswordRequestDto,
  UpdatePasswordResponse,
  VerifyPasswordResponse,
  UpdatePersonalInformationResponse,
} from 'components/auth/auth.dto';

export class AuthApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/accounts');
  }

  public async signUp(signUpDto: SignUpDto) {
    return this.instance.post('', signUpDto).then((response: AxiosResponse<any>) => response.data);
  }

  public async sendEmailVerificationCode() {
    return this.instance
      .post<SuccessResponse>('/email-verification')
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async verifyEmailVerificationCode(code: string): Promise<{ success: boolean }> {
    return this.instance
      .put<SuccessResponse>('/email-verification', {
        code: code,
      })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async login(signInDto: SignInDto) {
    if (signInDto.email && signInDto.password) {
      return this.instance.post('/login', signInDto).then((response: AxiosResponse<SessionInfoDto>) => response.data);
    } else if (signInDto.refreshToken) {
      return this.instance.post('/login', signInDto).then((response: AxiosResponse<SessionInfoDto>) => response.data);
    }
  }

  public async getCurrentAccount() {
    return this.instance.get(`/current-account`).then((response: AxiosResponse<AccountDto>) => response.data);
  }

  public async logout() {
    return this.instance.post('/logout').then((response: AxiosResponse<any>) => response.data);
  }

  public async requestResetPassword(email: string) {
    return this.instance
      .post('/reset-password', { email: email })
      .then((response: AxiosResponse<SuccessResponse>) => response.data);
  }

  public async resetPassword(password: string, resetPasswordToken: string) {
    return this.instance
      .put<SuccessResponse>('/reset-password', {
        password: password,
        resetPasswordToken: resetPasswordToken,
      })
      .then((response: AxiosResponse<SuccessResponse>) => response.data);
  }

  public async createPasswordValidationToken(password: string) {
    return this.instance
      .post(`/password-validation-token`, { password: password })
      .then((response: AxiosResponse<VerifyPasswordResponse>) => response.data);
  }

  public async sendUpdateEmailVerificationCode({
    email,
    accountPasswordValidationToken,
  }: SendUpdateEmailVerificationCodeRequestDto) {
    return this.instance
      .put(`/email/verification-code`, { email: email, accountPasswordValidationToken: accountPasswordValidationToken })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async updateEmailAddressByVerifyingCode(email: string, code: string): Promise<{ success: boolean }> {
    return this.instance
      .put(`/email`, { email: email, code: code })
      .then((response: AxiosResponse<any>) => response.data);
  }

  public async updatePassword({ oldPassword, newPassword }: UpdatePasswordRequestDto) {
    return this.instance
      .put(`/password`, { oldPassword: oldPassword, newPassword: newPassword })
      .then((response: AxiosResponse<UpdatePasswordResponse>) => response.data);
  }

  public async getInvitedUser(inviteToken: string) {
    return this.instance
      .get(`/invited-user/${inviteToken}`)
      .then((response: AxiosResponse<InvitedUser>) => response.data);
  }

  public async updatePersonalInformation(body: UpdatePersonalInformationResponse) {
    return this.instance.patch('/current-account', body).then((response: AxiosResponse<AccountDto>) => response.data);
  }
}
